import { HeartHandshake, PhoneCall, Send } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { api, createActionIdempotencyKey, isNetworkError } from "../api/client";
import type { Contact } from "../api/types";
import { CommandButton } from "../components/CommandButton";
import { InfoRow } from "../components/InfoRow";
import { PageShell } from "../components/PageShell";
import { StatusBlock } from "../components/StatusBlock";
import {
  DEFAULT_EMERGENCY_CONTACT,
  pickEmergencyContact,
} from "../contactFallback";
import styles from "../styles/App.module.css";

export function PeacePage() {
  const [message, setMessage] = useState("点下面按钮，给家人发送报平安信息。");
  const [tone, setTone] = useState<"info" | "error" | "success">("info");
  const [sending, setSending] = useState(false);
  const [emergencyContact, setEmergencyContact] = useState<Contact>(
    DEFAULT_EMERGENCY_CONTACT,
  );
  const pendingIdempotencyKey = useRef<string | null>(null);
  const sendingRef = useRef(false);

  useEffect(() => {
    let active = true;
    api
      .contacts()
      .then((result) => {
        if (active) {
          setEmergencyContact(pickEmergencyContact(result.items));
        }
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, []);

  async function sendPeaceReport() {
    if (sendingRef.current) {
      setMessage("正在发送报平安，请稍等。");
      setTone("info");
      return;
    }

    sendingRef.current = true;
    setSending(true);
    setMessage("正在发送报平安，请稍等。");
    setTone("info");
    pendingIdempotencyKey.current ??= createActionIdempotencyKey("peace");
    try {
      const result = await api.peaceReport(pendingIdempotencyKey.current);
      pendingIdempotencyKey.current = null;
      setMessage(result.message);
      setTone("success");
    } catch (error) {
      setMessage(
        isNetworkError(error)
          ? "网络暂时不可用。报平安不会离线排队发送，请恢复网络后再点一次；着急时请直接打电话给家人。"
          : error instanceof Error
            ? error.message
            : "报平安失败，请返回上一层后重试。",
      );
      setTone("error");
    } finally {
      sendingRef.current = false;
      setSending(false);
    }
  }

  return (
    <PageShell title="报平安" where="报平安页">
      <StatusBlock
        tone={tone}
        message={message}
        recoveryAction={
          tone === "error"
            ? "请重新点发送报平安信息，或直接拨打下面的紧急联系人电话。"
            : undefined
        }
      />
      <div className={styles.commandList}>
        <CommandButton
          icon={Send}
          title="发送报平安信息"
          hint={sending ? "正在发送，请稍等" : "双击发送报平安信息"}
          onClick={() => void sendPeaceReport()}
        />
        <a className={styles.linkButton} href={emergencyContact.tel_uri}>
          <PhoneCall aria-hidden="true" />
          打电话给紧急联系人：{emergencyContact.name}，号码{" "}
          {emergencyContact.phone_display}
        </a>
        <InfoRow
          icon={HeartHandshake}
          title="报平安说明"
          hint="发送成功只代表信息已经发送，家人阅读后会再联系。"
        />
      </div>
    </PageShell>
  );
}
